/**
 * offlineFinalEntries — the FINAL-pinned side of the OfflineIndicator's failed surface: which failed entries
 * the server refused for good (never offered "Erneut versuchen"), and the rep's discard of one of them.
 * Split from OfflineIndicator.tsx by cohesion (the component stays presentation, under the file-size gate).
 *
 * BOTH READS FAIL CLOSED AND LOUD (Codex P2s on #2213):
 *  - a failed finality lookup is `unknown`, never "none final": while finality is unknown no retry may be
 *    offered (it could re-send a refused report), the rep is told the status could not be read, and can re-check;
 *  - a discard that throws or is refused (capture not confirmed → the entry is KEPT) is shown on the entry,
 *    and is pending (disabled, aria-busy) until its outcome is known.
 * Every failure is reported through Sentry (the app's error sink), not only the console (D-33 R1).
 * Also home to the final-entry copy and the per-entry discard control the two surfaces render.
 */
import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { describeQueueEntries, discardEntry, type QueueEntryView } from '@appic/lib/offline/offlineQueue'
import { isGenuinelyFailed } from '@appic/lib/offline/queueStore'

/**
 * Why NO RETRY can move a failed entry. `refused`: the server refused it for good — its code (null: pinned before
 * codes existed). `not-sent`: it was NEVER offered to the server; it is pinned behind a visit report that did not
 * land (`predecessor-failed`) and moves only with that report — or leaves by discard (review of #2204, thread 16).
 */
export type NoRetry = { kind: 'refused'; code: string | null } | { kind: 'not-sent' }

/** The failed entries a retry cannot move, each with its discard exit: id → why. */
export type Finals = ReadonlyMap<string, NoRetry>

/** null = a retry may move it (no entry in Finals). One rule for both surfaces: genuinely failed and not retryable. */
function noRetryOf(v: QueueEntryView): NoRetry | null {
  if (!isGenuinelyFailed(v) || v.retryable) return null
  if (v.failureReason === 'predecessor-failed') return { kind: 'not-sent' }
  return { kind: 'refused', code: v.finalCode === undefined ? null : v.finalCode }
}

// The pill's label on ONE entry. A not-sent entry must not read as refused: the server never saw it.
export const NOT_SENT_LABEL = 'nicht gesendet'
export function entryLabel(why: NoRetry): string {
  if (why.kind === 'not-sent') return NOT_SENT_LABEL
  return why.code === null ? 'abgelehnt' : why.code
}

/** `loading`: THIS set's read is in flight · `known`: finality read · `unknown`: the read FAILED (fail closed). */
export type FinalsRead =
  | { state: 'loading' }
  | { state: 'known'; finals: Finals }
  | { state: 'unknown' }

const LOADING: FinalsRead = { state: 'loading' }
export const NO_FINALS: Finals = new Map()

// Rep copy for a FINAL refusal: no resend promise — contact the office, or discard.
export const FINAL_COPY = 'Vom Server abgelehnt. Wird nicht erneut gesendet. Bitte Büro kontaktieren oder verwerfen.'
// Rep copy for a NOT-SENT entry: it waits on a report that did not arrive — nothing here says the server refused it.
export const NOT_SENT_COPY = 'Nicht gesendet: gehört zu einem Besuchsbericht, der nicht angekommen ist. Bitte Büro kontaktieren oder verwerfen.'

/** The pill's paragraphs for the entries present: one per KIND, never the refusal copy for a not-sent entry. */
export function noRetryCopies(finals: Finals): string[] {
  const kinds = new Set([...finals.values()].map((why) => why.kind))
  return [...(kinds.has('refused') ? [FINAL_COPY] : []), ...(kinds.has('not-sent') ? [NOT_SENT_COPY] : [])]
}

/** The banner's sentence about the entries a retry cannot move ('' when there are none). */
export function noRetryBannerNote(finals: Finals): string {
  const refused = [...finals.values()].filter((why) => why.kind === 'refused').length
  const notSent = finals.size - refused
  const parts = [
    ...(refused > 0 ? [`${refused} vom Server abgelehnt`] : []),
    ...(notSent > 0 ? [`${notSent} nicht gesendet, weil der zugehörige Besuchsbericht nicht angekommen ist`] : []),
  ]
  return parts.length === 0 ? '' : ` — davon ${parts.join(', ')}: wird nicht erneut gesendet. Bitte Büro kontaktieren oder in der Liste verwerfen.`
}
// Finality unknown (the lookup failed): no retry is offered — say so, and let the rep re-check.
export const FINALS_UNKNOWN_COPY = 'Status der fehlgeschlagenen Einträge konnte nicht gelesen werden.'
export const RECHECK_LABEL = 'Status erneut prüfen'
const DISCARD_FAILED_COPY = 'Verwerfen fehlgeschlagen — Eintrag bleibt erhalten.'

/** The surfaces' share of an unknown finality: whether it is unknown, and the rep's re-check. */
export type Recheck = { finalsUnknown: boolean; onRecheck: () => void }

function reportOfflineError(action: string, err: unknown, extra: Record<string, unknown> = {}): void {
  console.error(`[OfflineIndicator] ${action}`, err, extra)
  Sentry.captureException(err, { tags: { surface: 'offline-indicator', action }, extra })
}

/** Finality of the given failed set, re-read whenever the SET changes or the rep asks to re-check. */
export function useFinalPinned(failedIds: readonly string[]): { read: FinalsRead; recheck: () => void } {
  const [attempt, setAttempt] = useState(0)
  const [read, setRead] = useState<{ ids: readonly string[]; attempt: number; read: FinalsRead } | null>(null)
  useEffect(() => {
    // Only the NEWEST lookup may speak (Codex P2 #2213). The set can change, or the rep re-check,
    // while a lookup is in flight; if the older one then settles LAST, its result — for a set that
    // is no longer current — would replace the newer one, the identity check below would read
    // "loading", and nothing re-runs the lookup: no retry, re-check or discard until the next queue
    // write. A superseded lookup is therefore ignored when it settles, whatever its outcome.
    let superseded = false
    describeQueueEntries() // the payload-free view (queueExit.toView), never the raw outbox
      .then((views) => {
        if (superseded) return
        const finals = new Map<string, NoRetry>()
        for (const v of views) {
          const why = noRetryOf(v)
          if (why !== null) finals.set(v.id, why)
        }
        setRead({ ids: failedIds, attempt, read: { state: 'known', finals } })
      })
      .catch((err: unknown) => {
        // A failed read is reported even when superseded: it is a real storage fault (D-33 R1).
        reportOfflineError('final-entry-lookup-failed', err, { failed: failedIds.length, superseded })
        if (superseded) return
        setRead({ ids: failedIds, attempt, read: { state: 'unknown' } })
      })
    return () => { superseded = true }
  }, [failedIds, attempt])
  const recheck = useCallback(() => setAttempt((n) => n + 1), [])
  // A read of an older set (or an older attempt) never speaks for the current one.
  const current = read !== null && read.ids === failedIds && read.attempt === attempt ? read.read : LOADING
  return { read: current, recheck }
}

/** `pending`: the discard is in flight · `failed`: it threw or was refused — the entry is KEPT. */
export type DiscardState = 'pending' | 'failed'

/** Discard of ONE final-pinned entry, with its visible in-flight / failed state per entry id. */
export function useDiscardFinal(): { discards: ReadonlyMap<string, DiscardState>; discard: (id: string) => void } {
  const [discards, setDiscards] = useState<ReadonlyMap<string, DiscardState>>(() => new Map())
  const mark = useCallback((id: string, state: DiscardState | null) => {
    setDiscards((prev) => {
      const next = new Map(prev)
      if (state === null) next.delete(id)
      else next.set(id, state)
      return next
    })
  }, [])
  const discard = useCallback((id: string) => {
    mark(id, 'pending')
    discardEntry(id)
      .then((outcome) => {
        if (outcome.discarded) { mark(id, null); return }
        reportOfflineError('discard-refused-kept', new Error(`discard of ${id} refused — KEPT`), { id, outcome })
        mark(id, 'failed')
      })
      .catch((err: unknown) => {
        reportOfflineError('discard-threw-kept', err, { id })
        mark(id, 'failed')
      })
  }, [mark])
  return { discards, discard }
}

/** One final-pinned entry's discard control: pending until the outcome is known, a refusal shown on the entry. */
export function DiscardControl({ state, onDiscard }: { state: DiscardState | undefined; onDiscard: () => void }) {
  return (
    <>
      {state === 'failed' && <span role="alert" className="shrink-0 font-sans text-destructive">{DISCARD_FAILED_COPY}</span>}
      <button type="button" onClick={onDiscard} disabled={state === 'pending'} aria-busy={state === 'pending'} className="ml-auto shrink-0 rounded-md px-1.5 py-0.5 font-sans font-semibold hover:bg-muted disabled:opacity-50">
        {state === 'pending' ? 'Wird verworfen…' : 'Verwerfen'}
      </button>
    </>
  )
}
